import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X, ArrowRight, Sparkles } from "lucide-react";
import { SITE, NAV_LINKS } from "@/lib/site";
import { CATEGORIES, MODULES, modulesIn, moduleHref } from "@/lib/modules";
import { Logo } from "@/components/ui-kit";
import { cn } from "@/lib/utils";

const MegaMenu = ({ onNavigate }) => (
  <div className="w-[1040px] rounded-2xl glass overflow-hidden shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)]" data-testid="nav-mega-menu">
    <div className="grid grid-cols-5 divide-x divide-mk-border/60">
      {CATEGORIES.map((c) => (
        <div key={c.slug} className="p-5">
          <Link to={`/platform/${c.slug}`} onClick={onNavigate} data-testid={`nav-mega-cat-${c.slug}`} className="group flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider text-mk-text hover:text-mk-lime transition-colors">
            <span className="grid place-items-center h-7 w-7 rounded-md bg-mk-surface border border-mk-border text-mk-lime group-hover:border-mk-lime/50"><c.icon className="h-3.5 w-3.5" /></span>{c.name}
          </Link>
          <ul className="mt-3 space-y-0.5">
            {modulesIn(c.slug).map((m) => (
              <li key={m.slug}>
                <Link to={moduleHref(m)} onClick={onNavigate} data-testid={`nav-mega-${m.slug}`} className="group flex items-center gap-2 rounded-md px-2 py-1.5 text-[12.5px] text-mk-dim hover:text-mk-text hover:bg-mk-raised/60 transition-colors">
                  <span className={cn("h-1.5 w-1.5 rounded-full shrink-0", m.flagship ? "bg-mk-lime" : "bg-mk-strong group-hover:bg-mk-dim")} />
                  <span className="truncate">{m.name}</span>
                  {m.isNew && <span className="ml-auto rounded-full bg-mk-lime/15 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-mk-lime shrink-0">New</span>}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
    <div className="flex items-center justify-between gap-4 border-t border-mk-border/60 bg-mk-bg/50 px-5 py-3">
      <div className="flex items-center gap-5 text-[11px] text-mk-muted">
        <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-mk-lime" />Flagship module</span>
        <span>{MODULES.length} modules · 5 lanes · one engine</span>
        <Link to="/#new" onClick={onNavigate} data-testid="nav-mega-new" className="inline-flex items-center gap-1.5 text-mk-lime hover:text-mk-text transition-colors"><span className="rounded-full bg-mk-lime/15 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider">New</span>H&S library · Properties · Marketplace · Accounts · Email inbox · Asset tags · AI RAMS · Toolbox talks</Link>
      </div>
      <div className="flex items-center gap-4">
        <Link to="/library" onClick={onNavigate} data-testid="nav-mega-library" className="inline-flex items-center gap-1.5 text-xs font-semibold text-mk-text hover:text-mk-lime transition-colors">Browse the H&S library</Link>
        <Link to="/help" onClick={onNavigate} data-testid="nav-mega-help" className="inline-flex items-center gap-1.5 text-xs font-semibold text-mk-text hover:text-mk-lime transition-colors">Help centre</Link>
        <Link to="/#ai-match" onClick={onNavigate} data-testid="nav-mega-ai-demo" className="inline-flex items-center gap-1.5 text-xs font-semibold text-mk-lime hover:text-mk-text transition-colors"><Sparkles className="h-3.5 w-3.5" />Try the AI match demo</Link>
        <Link to="/product" onClick={onNavigate} data-testid="nav-mega-all" className="text-xs text-mk-dim hover:text-mk-lime transition-colors inline-flex items-center gap-1">All modules <ArrowRight className="h-3 w-3" /></Link>
      </div>
    </div>
  </div>
);

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mega, setMega] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [mobileProduct, setMobileProduct] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setMobile(false); setMega(false); }, [pathname]);

  return (
    <header className={cn("fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300", scrolled || mobile ? "glass border-b" : "border-b border-transparent")} data-testid="navbar">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10 xl:px-12 h-[72px] flex items-center justify-between gap-4 xl:gap-6">
        <Link to="/" data-testid="nav-logo" aria-label="Fixlr home" className="shrink-0"><Logo /></Link>

        <nav className="hidden lg:flex items-center gap-0.5 xl:gap-1" onMouseLeave={() => setMega(false)}>
          {NAV_LINKS.map((l) =>
            l.mega ? (
              <div key={l.to} className="relative" onMouseEnter={() => setMega(true)}>
                <button
                  type="button"
                  data-testid="nav-product-trigger"
                  onClick={() => setMega((s) => !s)}
                  aria-expanded={mega}
                  className={cn("inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium transition-colors", pathname.startsWith("/product") || pathname.startsWith("/platform") || mega ? "text-mk-text bg-mk-raised/60" : "text-mk-dim hover:text-mk-text")}
                >
                  {l.label} <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", mega && "rotate-180")} />
                </button>
                <AnimatePresence>
                  {mega && (
                    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.18 }} className="absolute left-1/2 -translate-x-1/2 top-full pt-3">
                      <MegaMenu onNavigate={() => setMega(false)} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <NavLink key={l.to} to={l.to} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className={({ isActive }) => cn("rounded-full px-3 xl:px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap", isActive ? "text-mk-text bg-mk-raised/60" : "text-mk-dim hover:text-mk-text")}>
                {l.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-1.5 xl:gap-2 shrink-0 whitespace-nowrap">
          <a href={SITE.loginUrl} target="_blank" rel="noreferrer" data-testid="nav-signin-btn" className="rounded-full px-3 xl:px-4 py-2 text-sm font-medium text-mk-dim hover:text-mk-text transition-colors">Sign in</a>
          <span className="hidden xl:inline-flex"><Link to="/contact" data-testid="nav-book-demo-btn" className="btn-ghost !py-2 !px-4">Book a demo</Link></span>
          <a href={SITE.signupUrl} target="_blank" rel="noreferrer" data-testid="nav-start-free-trial-btn" className="btn-lime !py-2 !px-4">Start free trial <ArrowRight className="h-4 w-4" /></a>
        </div>

        <button type="button" className="lg:hidden grid place-items-center h-10 w-10 rounded-full border border-mk-border text-mk-text" onClick={() => setMobile((s) => !s)} data-testid="nav-mobile-toggle" aria-label="Menu">
          {mobile ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {mobile && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="lg:hidden overflow-hidden border-t border-mk-border/70" data-testid="nav-mobile-menu">
            <div className="px-5 py-4 space-y-1 max-h-[calc(100vh-72px)] overflow-y-auto">
              <button type="button" onClick={() => setMobileProduct((s) => !s)} className="w-full flex items-center justify-between rounded-lg px-3 py-3 text-base font-medium text-mk-text" data-testid="nav-mobile-product-toggle">Product <ChevronDown className={cn("h-4 w-4 transition-transform", mobileProduct && "rotate-180")} /></button>
              {mobileProduct && (
                <div className="pl-3 pb-2 space-y-3">
                  {CATEGORIES.map((c) => (
                    <div key={c.slug}>
                      <Link to={`/platform/${c.slug}`} className="block text-xs font-semibold uppercase tracking-wider text-mk-lime py-1">{c.name}</Link>
                      {modulesIn(c.slug).map((m) => <Link key={m.slug} to={moduleHref(m)} className="block py-1.5 text-sm text-mk-dim">{m.name}</Link>)}
                    </div>
                  ))}
                </div>
              )}
              {NAV_LINKS.filter((l) => !l.mega).map((l) => (
                <NavLink key={l.to} to={l.to} data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="block rounded-lg px-3 py-3 text-base font-medium text-mk-text">{l.label}</NavLink>
              ))}
              <NavLink to="/contact" className="block rounded-lg px-3 py-3 text-base font-medium text-mk-text">Contact</NavLink>
              <div className="pt-3 flex flex-col gap-2">
                <a href={SITE.signupUrl} target="_blank" rel="noreferrer" className="btn-lime w-full">Start free trial</a>
                <a href={SITE.loginUrl} target="_blank" rel="noreferrer" data-testid="mobile-nav-signin-btn" className="btn-ghost w-full">Sign in</a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
